import React, { useState, useEffect } from 'react';
import { Popconfirm, Table, Form, Input, Button, Space, Card, Tooltip, Col, message } from 'antd';
import { DeleteOutlined, EditOutlined, SearchOutlined, ReloadOutlined, PlusOutlined, KeyOutlined, MobileOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { roleService } from '../../services/roleService';
import RoleCreateForm from './RoleCreateForm';
import RoleEditForm from './RoleEditForm';
import RoleMenuAuthModal from './RoleMenuAuthModal';
import RolePdaMenuAuthModal from './RolePdaMenuAuthModal';
import FilterPanel from '../../components/common/FilterPanel';
import './index.css';

const RoleManagement = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [createFormVisible, setCreateFormVisible] = useState(false);
    const [editFormVisible, setEditFormVisible] = useState(false);
    const [menuAuthVisible, setMenuAuthVisible] = useState(false);
    const [pdaMenuAuthVisible, setPdaMenuAuthVisible] = useState(false);
    const [currentRole, setCurrentRole] = useState(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => `${t('common.operation')} ${range[0]}-${range[1]} / ${t('common.total')} ${total}`
    });
    const { t } = useTranslation();

    const columns = [
        {
            title: t('role.name'),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: t('role.code'),
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: t('role.description'),
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: t('role.creator'),
            dataIndex: 'creatorName',
            key: 'creatorName',
        },
        {
            title: t('role.createdAt'),
            dataIndex: 'gmtCreate',
            key: 'gmtCreate',
            render: (timestamp) => {
                if (!timestamp) return '-';
                const date = new Date(timestamp);
                return date.toLocaleString('zh-CN', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false
                }).replace(/\//g, '-');
            }
        },
        {
            title: t('role.actions'),
            key: 'actions',
            render: (record) => (
                <Space>
                    <Tooltip title={t('role.menuAuthorization')}>
                        <Button
                            type="link"
                            icon={<KeyOutlined />}
                            onClick={() => handleMenuAuth(record)}
                        />
                    </Tooltip>
                    <Tooltip title={t('role.pdaMenuAuthorization')}>
                        <Button
                            type="link"
                            icon={<MobileOutlined />}
                            onClick={() => handlePdaMenuAuth(record)}
                        />
                    </Tooltip>
                    <Tooltip title={t('role.edit')}>
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title={t('role.deleteConfirm')}
                        onConfirm={() => handleDelete(record.code)}
                        okText={t('common.confirm')}
                        cancelText={t('common.cancel')}
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                        >
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const fetchData = async (params = {}) => {
        setLoading(true);
        try {
            // 获取表单当前值
            const formValues = form.getFieldsValue();
            
            // 过滤掉空值和undefined值
            const filteredFormValues = Object.keys(formValues).reduce((acc, key) => {
                const value = formValues[key];
                if (value !== undefined && value !== null && value !== '') {
                    acc[key] = value;
                }
                return acc;
            }, {});

            const searchParams = {
                ...filteredFormValues,
                currentPage: params.currentPage || pagination.current,
                pageSize: params.pageSize || pagination.pageSize,
                type: "ROLE"
            };

            const response = await roleService.getRoles(searchParams);

            if (response.success) {
                setDataSource(response.data.tableData);
                setPagination(prev => ({
                    ...prev,
                    current: response.data.paging?.currentPage || 1,
                    total: response.data.paging?.totalCount || 0
                }));
            } else {
                message.error(response.errorMessage || t('role.queryFailed'));
            }
        } catch (error) {
            console.error('查询失败:', error);
            message.error(t('role.queryFailedRetry'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSearch = (values) => {
        // 将搜索条件设置到表单中
        form.setFieldsValue(values);
        setPagination(prev => ({ ...prev, current: 1 }));
        // 使用传入的values进行查询
        fetchData({ currentPage: 1 });
    };

    const handleReset = () => {
        form.resetFields();
        setPagination(prev => ({ ...prev, current: 1 }));
        // 重置后延迟一下再查询，确保表单已经重置完成
        setTimeout(() => {
            fetchData({ currentPage: 1 });
        }, 0);
    };

    const handleTableChange = (paginationConfig) => {
        const newPagination = {
            ...pagination,
            current: paginationConfig.current,
            pageSize: paginationConfig.pageSize
        };
        setPagination(newPagination);
        fetchData({
            currentPage: paginationConfig.current,
            pageSize: paginationConfig.pageSize
        });
    };

    const handleAdd = () => {
        setCreateFormVisible(true);
    };

    const handleEdit = (role) => {
        setCurrentRole(role);
        setEditFormVisible(true);
    };

    const handleMenuAuth = (role) => {
        setCurrentRole(role);
        setMenuAuthVisible(true);
    };

    const handlePdaMenuAuth = (role) => {
        setCurrentRole(role);
        setPdaMenuAuthVisible(true);
    };

    const handleDelete = async (code) => {
        try {
            const response = await roleService.deleteRole(code);
            if (response.success) {
                message.success(t('role.deleteSuccess'));
                fetchData();
            } else {
                message.error(response.errorMessage || t('role.deleteFailed'));
            }
        } catch (error) {
            console.error('删除失败:', error);
            message.error(t('role.deleteFailedRetry'));
        }
    };

    const handleCreateFormCancel = () => {
        setCreateFormVisible(false);
    };

    const handleCreateFormSuccess = () => {
        setCreateFormVisible(false);
        fetchData();
    };

    const handleEditFormCancel = () => {
        setEditFormVisible(false);
        setCurrentRole(null);
    };

    const handleEditFormSuccess = () => {
        setEditFormVisible(false);
        setCurrentRole(null);
        fetchData();
    };

    const handleMenuAuthCancel = () => {
        setMenuAuthVisible(false);
        setCurrentRole(null);
    };

    const handleMenuAuthSuccess = () => {
        setMenuAuthVisible(false);
        setCurrentRole(null);
        fetchData(); // 刷新数据以获取最新的菜单权限信息
    };

    const handlePdaMenuAuthCancel = () => {
        setPdaMenuAuthVisible(false);
        setCurrentRole(null);
    };

    const handlePdaMenuAuthSuccess = () => {
        setPdaMenuAuthVisible(false);
        setCurrentRole(null);
        fetchData(); // 刷新数据以获取最新的菜单权限信息
    };

    return (
        <div>
            <FilterPanel
                title={t('role.title')}
                onFilter={handleSearch}
                onReset={handleReset}
                initialValues={{}}
                className="role-search"
                form={form}
            >
                <Col span={6}>
                    <Form.Item name="name" label={t('role.name')}>
                        <Input placeholder={t('role.name') + t('common.search')} />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item name="code" label={t('role.code')}>
                        <Input placeholder={t('role.code') + t('common.search')} />
                    </Form.Item>
                </Col>
            </FilterPanel>

            <Card className="role-table">
                <div className="table-actions">
                    <Button 
                        type="primary" 
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                    >
                        {t('role.new')}
                    </Button>
                </div>
                <Table
                    columns={columns}
                    dataSource={dataSource}
                    loading={loading}
                    pagination={pagination}
                    onChange={handleTableChange}
                    rowKey="id"
                    scroll={{ x: 1200, y: 500 }}
                />
            </Card>

            <RoleCreateForm
                visible={createFormVisible}
                onCancel={handleCreateFormCancel}
                onSuccess={handleCreateFormSuccess}
            />

            <RoleEditForm
                visible={editFormVisible}
                role={currentRole}
                onCancel={handleEditFormCancel}
                onSuccess={handleEditFormSuccess}
            />

            <RoleMenuAuthModal
                visible={menuAuthVisible}
                role={currentRole}
                onCancel={handleMenuAuthCancel}
                onSuccess={handleMenuAuthSuccess}
            />
            
            <RolePdaMenuAuthModal
                visible={pdaMenuAuthVisible}
                role={currentRole}
                onCancel={handlePdaMenuAuthCancel}
                onSuccess={handlePdaMenuAuthSuccess}
            />
        </div>
    );
};

export default RoleManagement;